/**
 * @class BCMSModalItem
 * @classdesc Class definition for BCMSModalItem
 * @param {STRING} id - The id of the modal item
 * @param {React.Component} modal - The component function of the modal
 * 
 * @property {STRING} _id - The id of the modal item
 * @property {React.Component} _component - The component function of the modal
 * @property {BOOLEAN} _isActive - Boolean value to determine if the modal is shown or not
 * @property {OBJECT} _data - The data that should be passed to the modal when opened
 */
export default class BCMSModalItem {
    _id;
    _component;
    _isActive;
    _data;

    constructor(id, modal) {
        this.id = id;
        this.component = modal;
        this.isActive = false;
        this.data = undefined;
    }

    /**
     * @function id
     * @instance
     * @param {STRING} value - Value that should be set for the id property
     * @description Setter for the id property
     * @memberof BCMSModalItem
     */
    set id(value) { this._id = value; }

    /**
     * @function component
     * @instance
     * @param {React.Component} value - The modal should be set to the component property
     * @description Setter for the component property
     * @memberof BCMSModalItem
     */
    set component(value) { this._component = value; }

    /**
     * @function isActive
     * @instance
     * @param {BOOLEAN} value - This boolean value that should be set to the isActive property
     * @description Setter for the isActive property
     * @memberof BCMSModalItem
     */
    set isActive(value) { this._isActive = value; }

    /**
     * @function data
     * @instance
     * @param {OBJECT} value - The value that should be set for the data property
     * @description Setter for the data property
     * @memberof BCMSModalItem
     */
    set data(value) { this._data = value; }

    /**
     * @function id
     * @instance
     * @returns {STRING}
     * @description Getter for the id property
     * @memberof BCMSModalItem
     */
    get id() { return this._id; }

    /**
     * @function component
     * @instance
     * @returns {React.Component}
     * @description Getter for the component property
     * @memberof BCMSModalItem
     */
    get component() { return this._component; }

    /**
     * @function isActive
     * @instance
     * @returns {BOOLEAN}
     * @description Getter for the isActive property
     * @memberof BCMSModalItem
     */
    get isActive() { return this._isActive; }

    /**
     * @function data
     * @instance
     * @returns {OBJECT}
     * @description Getter for the data property
     * @memberof BCMSModalItem
     */
    get data() { return this._data; }

    /**
     * @function open
     * @instance
     * @param {OBJECT} data - The data to be set and passed to the modal when opened
     * @returns {NULL}
     * @description Open the modal by setting the isActive and data properties
     * @memberof BCMSModalItem
     */
    open(data) {
        this.data = data;
        this.isActive = true;
    }

    /**
     * @function close
     * @instance
     * @returns {NULL}
     * @description Close the modal by setting the isActive property
     * @memberof BCMSModalItem
     */
    close() {
        this.isActive = false;
    }
}